/**
 * PostHog analytics service — single source of truth for product analytics.
 *
 * Design decisions (see docs/sprint-retrospectives/2026-04-19-sprint-3-day-4.md
 * and docs/runbooks/analytics-posthog.md for rationale):
 *
 * 1. **Singleton pattern** — never import posthog-js directly elsewhere.
 *    Call `trackEvent()` / `identifyUser()` / `resetUser()` from here so
 *    we can change the backend (or turn it off entirely) in one place.
 *
 * 2. **No-op without VITE_POSTHOG_KEY** — lets us ship the instrumentation
 *    well before Don pastes real keys into Vercel. Never throws, never
 *    breaks the app if PostHog is unreachable.
 *
 * 3. **Session replay explicitly disabled** — Sentry Session Replay
 *    already records every session. PostHog's replay would be duplicate
 *    load + double GDPR surface area.
 *
 * 4. **Autocapture on** — free pageviews and clicks. Explicit events
 *    supplement for activation milestones that autocapture can't infer.
 *
 * 5. **No PII in events** — `identify` gets user.id + role + org_id +
 *    signup_source, never email/name. Customer Content (project names,
 *    annotator names) never goes into event properties — IDs only.
 *
 * 6. **Group analytics via org_id property AND `posthog.group()` call.**
 *    Both so funnels work whether or not the PostHog account ever
 *    upgrades to the group-analytics-paid-addon tier — property filters
 *    give us per-org funnels on free, groups unlock native B2B insights
 *    on paid.
 *
 * 7. **env-aware** — `env` property on every event distinguishes
 *    staging / deltra / prod so dashboards can filter out non-customer
 *    noise cleanly.
 */

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY || '';
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
const APP_ENV = import.meta.env.VITE_APP_ENV || (import.meta.env.PROD ? 'prod' : 'dev');

// Lazy-loaded SDK — keeps ~70KB out of the initial bundle.
let posthogInstance = null;
let initPromise = null;
let identityCache = null; // last known { userId, role, orgId } so we can replay on re-init

function isEnabled() {
  return typeof window !== 'undefined' && !!POSTHOG_KEY && APP_ENV !== 'test';
}

/**
 * Boot PostHog. Safe to call multiple times — second call is a no-op.
 * Returns a promise resolving to the posthog instance (or null when disabled).
 */
export async function initPostHog() {
  if (!isEnabled()) return null;
  if (posthogInstance) return posthogInstance;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const mod = await import('posthog-js');
      const posthog = mod.default || mod;

      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,

        // Privacy & data hygiene
        persistence: 'localStorage+cookie',
        autocapture: true,
        capture_pageview: true,
        capture_pageleave: true,

        // We already have Sentry session replay; don't duplicate
        disable_session_recording: true,

        // Don't track external sites embedding our iframes
        cross_subdomain_cookie: false,

        // Mask input values by default — any text field is untrusted
        // (annotators, admins type sensitive content all the time)
        mask_all_text: false, // still capture headings / labels for UX debugging
        mask_all_element_attributes: false,

        // Tag every event with environment so dashboards can filter
        // staging / deltra from prod cleanly
        loaded: (instance) => {
          instance.register({ env: APP_ENV });
          // Replay any identity that was set before init finished
          if (identityCache) {
            applyIdentity(instance, identityCache);
          }
        },
      });

      posthogInstance = posthog;
      return posthog;
    } catch (err) {
      // Never break the app because analytics is unreachable
      console.warn('[posthog] init failed:', err?.message || err);
      return null;
    }
  })();

  return initPromise;
}

function applyIdentity(instance, { userId, role, orgId, signupSource, createdAt, orgName, planTier }) {
  if (!instance || !userId) return;
  // Never send email / name here — non-PII only
  instance.identify(userId, {
    role: role || 'unknown',
    org_id: orgId || null,
    signup_source: signupSource || null,
    created_at: createdAt || null,
  });
  if (orgId) {
    instance.group('organization', orgId, {
      name: orgName || orgId,
      plan_tier: planTier || 'starter',
    });
  }
}

/**
 * Identify the logged-in user. Called from AuthContext after session sync.
 * Non-PII only — user.id, role, org_id, signup_source.
 */
export async function identifyUser({ userId, role, orgId, signupSource, createdAt, orgName, planTier }) {
  identityCache = { userId, role, orgId, signupSource, createdAt, orgName, planTier };
  if (!isEnabled()) return;
  const instance = await initPostHog();
  if (!instance) return;
  applyIdentity(instance, identityCache);
}

/**
 * Reset on logout — clears distinct_id + group so next user gets a
 * fresh identity (prevents cross-user event mixing on shared devices).
 */
export async function resetUser() {
  identityCache = null;
  if (!isEnabled()) return;
  const instance = await initPostHog();
  if (!instance) return;
  try {
    instance.reset();
  } catch (err) {
    console.warn('[posthog] reset failed:', err?.message || err);
  }
}

/**
 * Track a product event. Properties auto-merged with `env`, `org_id`,
 * `role` so every event is filterable from day 1.
 *
 * @param {string} eventName — past-tense verb (signup_completed, project_created)
 * @param {Object} properties — additional properties; keep to IDs, booleans,
 *                              enum-style strings. NEVER include names / emails /
 *                              any Customer Content.
 */
export async function trackEvent(eventName, properties = {}) {
  const merged = {
    env: APP_ENV,
    ...(identityCache?.orgId ? { org_id: identityCache.orgId } : {}),
    ...(identityCache?.role ? { role: identityCache.role } : {}),
    ...properties,
  };

  // Dev-mode visibility: let engineers see events firing locally even
  // when no VITE_POSTHOG_KEY is set. Silenced in prod/staging to keep
  // real user consoles clean.
  if (import.meta.env.DEV) {
    console.log(`[analytics] ${eventName}`, merged);
  }

  if (!isEnabled()) return;
  const instance = await initPostHog();
  if (!instance) return;
  try {
    instance.capture(eventName, merged);
  } catch (err) {
    console.warn(`[posthog] capture ${eventName} failed:`, err?.message || err);
  }
}

// Expose a debug helper for manual verification during QA.
// Open devtools console, run: window.__juuniPosthog?.debug()
if (typeof window !== 'undefined' && isEnabled()) {
  window.__juuniPosthog = {
    async debug() {
      const p = await initPostHog();
      if (p?.debug) p.debug();
      return p;
    },
    async getDistinctId() {
      const p = await initPostHog();
      return p?.get_distinct_id?.() || null;
    },
  };
}
